/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import classNames from 'clsx'
import {
  arrayOf,
  bool,
  func,
  number,
  oneOfType,
  shape,
  string,
} from 'prop-types'
import { useRef } from 'react'

import IconCaretDown from 'views/components/atoms/Icon/IconCaretDown'
import IconCross from 'views/components/atoms/Icon/IconCross'
import { CLIENTSIDE_HOOK_FLOATING_DROPDOWN_ENABLED } from 'views/components/molecules/Select/consts'

import NativeSelect from './NativeSelect'
import OptionContent from './OptionContent'

import './Select.scss'

const Select = props => {
  const {
    id,
    label,
    showLabelColon,
    disabled,
    selectedValue,
    options,
    showLabel,
    floatingLabel,
    smaller,
    onChange,
    className,
    allowLinks,
    forceDefaultSelection,
    dataQa,
    errorMessage,
    volumePricing,
    basePrice,
  } = props

  // The props will be spread to a native HTML element where hasOverdraft is not allowed
  const {
    hasOverdraft,
    hasOverdraftMaxed,
    isSelectedGreyedOut,
    floatingDropDown,
    id: selectId,
    ...nativeSelectProps
  } = props

  const replacedRef = useRef()

  if (onChange && allowLinks) {
    throw Error(
      "Don't use `onChange` and `allowLinks` props at the same time. Defining `onChange` breaks navigation behaviour."
    )
  }

  let defaultValue = selectedValue
  // Be wary here: selectedValue could be an empty string or 0 which sometimes leads to two options
  // being selected if the selected value is not at the start of the options!
  // defaultValue == null checks if selectedValue is null or undefined
  if (defaultValue == null && forceDefaultSelection) {
    defaultValue = options[0].value
  }

  let selectedItem = options.find(item => item.value === defaultValue)

  if (!selectedItem && forceDefaultSelection) {
    ;[selectedItem] = options
    defaultValue = selectedItem.value
  }

  const hasError = Boolean(errorMessage)

  const labelClassnameMedium = classNames(
    'u-font-weight--bold',
    'm-Select__label--medium',
    'u-margin--right',
    {
      'u-visuallyhidden': !showLabel,
      'u-hidden': floatingLabel,
      'u-hidden@small-tiny': !floatingLabel,
    }
  )
  const classes = classNames(className, 'm-Select__wrapper', {
    'm-Select--withLabel l-flex': showLabel && label !== '',
    'm-Select--withFloatingLabel': floatingLabel,
    'm-Select--smaller': smaller,
    'm-Select--red': hasOverdraftMaxed || hasError,
    'm-Select--disabled': disabled,
    'm-Select--orange': hasOverdraft && !hasOverdraftMaxed,
  })
  const labelClassnameSmallTiny = classNames(
    'a-h4',
    'm-Select__label',
    'u-font-weight--normal',
    {
      'u-hidden@medium': !floatingLabel,
      'u-visuallyhidden': !showLabel,
    }
  )
  const replacedClassName = classNames('m-Select__replaced', {
    'is-disabled': disabled,
  })

  const selectedOptionClassName = classNames(
    'm-Select__item m-Select__item--selectable m-Select__item__selected-item u-padding-xx-large--right',
    {
      'u-color--grey': isSelectedGreyedOut,
    }
  )

  const iconCaretClassName = classNames({
    'u-color--blue': !hasError,
    'u-color--negative': hasError,
  })

  return (
    <>
      <div
        className={classes}
        data-clientside-hook="select-container nojs-trigger"
        data-qa-id={dataQa}
      >
        <label id={`${id}-label`} htmlFor={id} className={labelClassnameMedium}>
          {`${label}${showLabelColon ? ':' : ''}`}
        </label>
        <label
          id={`${id}-label`}
          htmlFor={id}
          className={labelClassnameSmallTiny}
        >
          {`${label}${showLabelColon ? ':' : ''}`}
        </label>
        <div className="m-Select__container u-position--relative">
          <NativeSelect {...nativeSelectProps} defaultValue={defaultValue} />
          <div className="m-Select__arrow u-transform-center--y">
            <IconCaretDown size="small" className="u-color--blue" />
          </div>
        </div>
        <div
          className={replacedClassName}
          data-clientside-hook="select-replace"
          // @ts-ignore
          tabIndex="0"
          id={id}
          ref={replacedRef}
          role="combobox"
          aria-labelledby={`${id}-label`}
          aria-controls={`${id}-options-list`}
          aria-expanded="false"
          aria-activedescendant={`${id}-option-${selectedValue}`}
          aria-haspopup="listbox"
        >
          <div className="m-Select__inner l-flex l-flex--distribute-space">
            <div
              className={selectedOptionClassName}
              data-value={defaultValue}
              data-clientside-hook="select-selected-item"
            >
              {selectedItem?.text || label}
            </div>
            <div className="m-Select__arrow u-transform-center--y">
              <IconCaretDown size="small" className={iconCaretClassName} />
            </div>
          </div>
          <div
            className="m-Select__list m-Select__list--dropdown"
            data-clientside-hook={classNames('select-drop-down', {
              [CLIENTSIDE_HOOK_FLOATING_DROPDOWN_ENABLED]: floatingDropDown,
            })}
          >
            <div className="m-Select__dropdown--label l-flex u-hidden@medium">
              <div className="l-flex__primary a-h1 u-no-margin--bottom">
                {label}
              </div>
              <div data-clientside-hook="select-close-button">
                <IconCross className="l-flex__fixed u-color--blue" />
              </div>
            </div>
            <div
              className="m-Select__list"
              role="listbox"
              aria-label={label}
              id={`${id}-options-list`}
              aria-multiselectable="false"
            >
              {options.map((option, idx) => (
                <OptionContent
                  {...option}
                  role="option"
                  data-clientside-hook="select-item"
                  data-value={option.value}
                  data-upid={option.upid}
                  aria-posinset={idx + 1}
                  aria-setsize={options.length}
                  aria-selected={option.value === defaultValue}
                  aria-disabled={disabled}
                  isSelected={option.value === defaultValue}
                  volumePricing={volumePricing}
                  allowLinks={allowLinks}
                  key={option.value}
                  basePrice={basePrice}
                  id={`${id}-option-${option.value}`}
                  onClick={
                    onChange && !option.disabled
                      ? () => {
                          onChange({ target: { value: option.value } })
                        }
                      : null
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      {hasError && (
        <div
          id={`${id}-error`}
          data-qa-id={`${dataQa}-error`}
          className="u-margin-x-small--top"
        >
          <p className="a-p u-color--negative">{errorMessage}</p>
        </div>
      )}
    </>
  )
}

Select.propTypes = {
  id: string.isRequired,
  name: string.isRequired,
  disabled: bool,
  label: string.isRequired,
  selectedValue: oneOfType([string, number]),
  options: arrayOf(
    shape({
      value: oneOfType([string, number]).isRequired,
      text: string.isRequired,
      disabled: bool,
    })
  ).isRequired,
  showLabel: bool,
  showLabelColon: bool,
  floatingLabel: bool,
  smaller: bool,
  onChange: func,
  className: string,
  allowLinks: bool,
  forceDefaultSelection: bool,
  hasOverdraft: bool,
  hasOverdraftMaxed: bool,
  dataQa: string,
  isSelectedGreyedOut: bool,
  floatingDropDown: bool,
  errorMessage: string,
  volumePricing: arrayOf(shape({})),
  basePrice: number,
}

Select.defaultProps = {
  selectedValue: null,
  disabled: false,
  showLabel: true,
  showLabelColon: true,
  floatingLabel: null,
  smaller: false,
  onChange: null,
  className: '',
  allowLinks: false,
  forceDefaultSelection: true,
  hasOverdraft: false,
  dataQa: undefined,
  isSelectedGreyedOut: false,
  floatingDropDown: false,
  errorMessage: '',
}

export default Select
